
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, ShieldCheck, Gauge, Clock, FileText, BarChart3 } from "lucide-react";
import { motion } from "framer-motion";
import { LainaCta } from "@/components/common/LainaCta";
import RelatedArticles from "@/components/articles/RelatedArticles";
import { useIsMobile } from "@/hooks/use-mobile";
import { YhdistelylainaHero } from "@/components/loan/combine/YhdistelylainaHero";
import { YhdistelylainaFAQ } from "@/components/loan/combine/YhdistelylainaFAQ";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute } from "@/utils/enhancedSeoUtils";

const YhdistelylainaPage = () => {
  const isMobile = useIsMobile();
  const seoData = getEnhancedSeoDataForRoute("/lainat/yhdistelylaina");

  return (
    <>
      <EnhancedSeoMetadataComponent
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="https://lainasampo.fi/lainat/yhdistelylaina"
        schema={seoData.schema}
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero section with calculator */}
        <YhdistelylainaHero />

        {/* Main content section - positioned to partially overlap with hero and pushed a bit lower */}
        <div className="container mx-auto px-4 md:px-6 relative -mt-4 md:-mt-6 lg:-mt-8 z-10">
          <Link
            to="/lainatuotteet"
            className="inline-flex items-center mb-6 font-medium text-white hover:text-blue-300 md:text-white lg:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Takaisin lainatuotteisiin
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8 mb-8">
            <div className="lg:col-span-2 space-y-6 md:space-y-10">
              <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 bg-white p-6 rounded-xl shadow-sm">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 md:mb-4">Yhdistelylaina helpottaa talouttasi</h2>
                  <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4">
                    Yhdistelylaina on joustava ratkaisu, kun haluat selkeyttää taloutesi hallintaa ja saada edullisemman kokonaiskuvan lainoistasi.
                  </p>
                  <div className="space-y-2 md:space-y-3">
                    <h3 className="font-bold text-blue-700 text-sm md:text-base">Yhdistelylainan edut:</h3>
                    <ul className="space-y-1 md:space-y-2">
                      {[
                        "Alhaisempi kokonaiskorko",
                        "Vain yksi kuukausierä",
                        "Selkeämpi talouden hallinta", 
                        "Helpompi seurata lainojen lyhentymistä",
                        "Pienempi kuukausittainen maksurasite"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start">
                          <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                          <span className="text-sm md:text-base text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 md:mb-4">Kilpailuta yhdistelylainasi helposti</h2>
                  <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4">
                    Lainasampo.fi välittää lainahakemuksesi yli 20 pankille ja rahoitusyhtiölle. Vertaile tarjouksia rauhassa ja valitse itsellesi sopivin vaihtoehto.
                  </p>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 md:p-5 rounded-xl border border-blue-200">
                    <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-sm md:text-base">Lainan tiedot</h3>
                    <div className="space-y-2 md:space-y-3">
                      {[
                        { label: "Lainasumma:", value: "2 000€ - 60 000€" },
                        { label: "Laina-aika:", value: "1-15 vuotta" },
                        { label: "Korko alkaen:", value: "5,9%" },
                        { label: "Vakuus:", value: "Vakuudeton" }
                      ].map((item, i) => (
                        <div key={i} className="flex justify-between text-sm md:text-base">
                          <span className="text-gray-700 font-medium">{item.label}</span>
                          <span className="font-bold text-blue-800">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
              
              <section className="bg-white p-6 md:p-8 rounded-xl shadow-md border border-gray-100">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 relative">
                  <span className="text-gray-800">
                    Miksi yhdistelylaina kannattaa?
                  </span>
                  <div className="absolute -bottom-2 md:-bottom-3 left-0 w-16 md:w-20 h-1 bg-blue-500"></div>
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-lg border border-blue-100">
                    <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center mb-4">
                      <FileText className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-blue-700 mb-2">Joustava rahoitus</h3>
                    <p className="text-gray-700">
                      Kun lainasi ovat yhdessä paikassa, saat selkeän kuvan taloudestasi ja jäljellä olevasta lainasummasta. 
                      Yhdistelylaina tekee lainanhoitamisesta helpompaa ja ymmärrettävämpää.
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-white p-5 rounded-lg border border-green-100">
                    <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center mb-4">
                      <BarChart3 className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-green-700 mb-2">Säästä kustannuksissa</h3>
                    <p className="text-gray-700">
                      Yhdistelylainan korko on tyypillisesti edullisempi kuin monien kulutusluottojen tai luottokorttien korot. 
                      Kilpailuttamalla voit säästää jopa tuhansia euroja lainasi kokonaiskustannuksissa.
                    </p>
                  </div>
                </div>
              </section>

              <YhdistelylainaFAQ />
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <div className="relative overflow-hidden rounded-xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800"></div>
                  <div className="absolute top-0 left-0 w-full h-full opacity-10">
                    <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                      <path d="M0,0 L100,0 L100,100 L0,100 Z" fill="url(#pattern)" />
                      <defs>
                        <pattern id="pattern" patternUnits="userSpaceOnUse" width="10" height="10">
                          <circle cx="5" cy="5" r="1" fill="white" />
                        </pattern>
                      </defs>
                    </svg>
                  </div>
                  <div className="relative p-6 text-white">
                    <h2 className="text-2xl font-bold mb-4">Kilpailuta yhdistelylainasi nyt!</h2>
                    <p className="mb-6">
                      Lainan kilpailutuksessa voit säästää jopa 30% lainasi kokonaiskustannuksissa!
                    </p>
                    <LainaCta 
                      variant="primary" 
                      size="lg" 
                      text="Hae yhdistelylainaa" 
                      className="w-full bg-white text-blue-700 hover:bg-blue-50 font-bold shadow-md"
                    />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h3 className="font-bold text-lg mb-4 text-gray-800">Katso myös nämä rahoitusvaihtoehdot</h3>
                  <div className="space-y-3">
                    {[
                      { name: "Kulutusluotto", url: "/lainat/kulutusluotto" },
                      { name: "Autolaina", url: "/lainat/autolaina" },
                      { name: "Remonttilaina", url: "/lainat/remonttilaina" },
                      { name: "Joustoluotto", url: "/lainat/joustoluotto" }
                    ].map((option, i) => (
                      <Link
                        key={i}
                        to={option.url}
                        className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-100 text-gray-800 hover:text-blue-700 font-medium transition-colors group"
                      >
                        <span>{option.name}</span>
                        <ArrowRight className="ml-auto h-4 w-4 text-gray-400 group-hover:text-blue-500 transform group-hover:translate-x-1 transition-all" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl md:rounded-2xl overflow-hidden mb-8 md:mb-16 relative">
            <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
              <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                <path fill="white" d="M47.1,-57.8C58.5,-45.7,63.7,-28.7,66.8,-11.1C70,6.5,71.1,24.5,63.5,38.6C55.9,52.7,39.6,62.8,22.2,67.6C4.9,72.3,-13.5,71.6,-29.6,64.7C-45.8,57.8,-59.6,44.7,-68.5,28C-77.4,11.3,-81.3,-9,-74.5,-25.3C-67.8,-41.6,-50.5,-54,-33.7,-65.1C-16.9,-76.2,-0.6,-86,14.8,-83.5C30.3,-81,35.7,-69.9,47.1,-57.8Z" />
              </svg>
            </div>
            <div className="relative p-5 md:p-8 text-white">
              <h2 className="text-2xl md:text-3xl font-bold mb-2 md:mb-4">Aloita yhdistelylainan hakeminen tänään!</h2>
              <p className="text-sm md:text-lg mb-4 md:mb-6 max-w-2xl">
                Kilpailuta lainatarjoukset ilmaiseksi ja ilman sitoumuksia. Saat lainapäätöksen nopeasti ja voit valita juuri sinulle sopivan rahoitusvaihtoehdon.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <LainaCta 
                  text={isMobile ? "Hae rahoitusta" : "Hae yhdistelylainaa"} 
                  className="bg-white text-blue-700 hover:bg-blue-50 font-bold"
                  variant="primary"
                  size={isMobile ? "md" : "lg"}
                />
                <Link to="/lainatuotteet" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-blue-700 hover:bg-blue-800 border border-blue-500 text-white font-medium transition-all duration-300">
                  Vertaile lainatuotteita <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </section>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <RelatedArticles
              productType="yhdistelylaina"
              title="Lue lisää yhdistelylainasta"
            />
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default YhdistelylainaPage;
